import * as Sentry from '@sentry/nextjs'

const LEGACY_HOST_MAP: Record<string, string> = {
  'shots.martini.video': 'cdn.martini.film',
  'staging.martini.video': 'staging-cdn.martini.film',
}

const reportedCallSites = new Set<string>()

/**
 * Result cache. This function sits on white-hot paths — thumbnail URL
 * resolution runs per asset per React render, and a profiling pass on a
 * 1,673-asset board measured ~447k calls (≈380ms of raw `new URL` parsing
 * plus the GC pressure of the discarded objects) during a single 1.6s pan.
 * The rewrite is pure string→string, so cache it. Bounded: on overflow the
 * first-inserted half is dropped (URLs are stable per session; churn is rare).
 */
const REWRITE_CACHE_MAX = 10_000
const rewriteCache = new Map<string, string>()

/**
 * Rewrites legacy `*.martini.video` storage hostnames to the new `*.martini.film`
 * subdomains. Idempotent — applying twice on the same URL returns the same result.
 *
 * The optional `callSite` is used to deduplicate observability reports so we get
 * at most one Sentry capture per session per call site (avoids per-frame spam from
 * canvas rendering loops while still surfacing where legacy URLs are flowing from).
 */
export function rewriteLegacyStorageHost(url: string, callSite?: string): string {
  if (!url || (!url.startsWith('http://') && !url.startsWith('https://'))) {
    return url
  }

  const cached = rewriteCache.get(url)
  if (cached !== undefined) return cached

  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return url
  }

  const newHost = LEGACY_HOST_MAP[parsed.hostname]
  if (!newHost) {
    cacheRewrite(url, url)
    return url
  }

  const legacyHost = parsed.hostname
  parsed.hostname = newHost
  const rewritten = parsed.toString()

  Sentry.addBreadcrumb({
    category: 'storage.legacy-host-rewrite',
    message: `Rewrote ${legacyHost} → ${newHost}`,
    data: { legacy_host: legacyHost, new_host: newHost, call_site: callSite ?? 'unknown' },
    level: 'info',
  })

  const dedupeKey = `${legacyHost}:${callSite ?? 'unknown'}`
  if (!reportedCallSites.has(dedupeKey)) {
    reportedCallSites.add(dedupeKey)
    try {
      Sentry.captureMessage('Legacy storage host rewrite', {
        level: 'info',
        tags: { legacy_host: legacyHost, new_host: newHost, call_site: callSite ?? 'unknown' },
        fingerprint: ['legacy-storage-rewrite', legacyHost, callSite ?? 'unknown'],
      })
    } catch {
      // Never let Sentry crash the rewrite path
    }
  }

  cacheRewrite(url, rewritten)
  return rewritten
}

function cacheRewrite(key: string, value: string): void {
  if (rewriteCache.size >= REWRITE_CACHE_MAX) {
    // Drop the older half (Map preserves insertion order).
    let toDrop = REWRITE_CACHE_MAX / 2
    for (const k of rewriteCache.keys()) {
      rewriteCache.delete(k)
      if (--toDrop <= 0) break
    }
  }
  rewriteCache.set(key, value)
}
